(() => {
  'use strict';

  let navListenersBound = false;
  let cachedHeaderOffset = null;
  let fadeToken = 0;
  const SCROLL_EDGE_TOLERANCE = 2;
  const POINTER_SCROLL_TOLERANCE = 8;
  const TAB_SCROLL_SETTLE_MS = 320;
  const scrollFinalizeState = new WeakMap();

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  function getFixedHeaderOffset() {
    if (cachedHeaderOffset != null) return cachedHeaderOffset;
    try {
      const candidates = [
        'header[sticky]',
        'header[role="banner"]',
        'header.header',
        '.shopify-section-header-sticky',
        '#shopify-section-header',
        '.section-header',
        'sticky-header'
      ];
      for (const sel of candidates) {
        const el = document.querySelector(sel);
        if (!el) continue;
        const style = window.getComputedStyle(el);
        const isVisible = el.offsetHeight > 0;
        const isFixed =
          style.position === 'fixed' ||
          style.position === 'sticky' ||
          el.classList.contains('shopify-section-header-sticky');
        if (isVisible && isFixed) {
          cachedHeaderOffset = el.offsetHeight || 0;
          return cachedHeaderOffset;
        }
      }
    } catch {}
    cachedHeaderOffset = 0;
    return 0;
  }

  window.addEventListener(
    'resize',
    () => {
      cachedHeaderOffset = null;
    },
    { passive: true }
  );

  function scrollComponentIntoViewTop(comp, extraOffset = 12) {
    if (!comp) return;
    const headerOffset = getFixedHeaderOffset();
    const offset = headerOffset + (typeof extraOffset === 'number' ? extraOffset : 0);
    const rect = comp.getBoundingClientRect();
    const absoluteTop = rect.top + window.pageYOffset;
    const targetTop = Math.max(absoluteTop - offset, 0);
    window.scrollTo({ top: targetTop, behavior: 'smooth' });
  }

  function isFullyInViewport(el) {
    if (!el) return true;
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    return r.top >= 0 && r.left >= 0 && r.bottom <= vh && r.right <= vw;
  }

  function slugify(text) {
    const map = { ä: 'ae', Ä: 'ae', ö: 'oe', Ö: 'oe', ü: 'ue', Ü: 'ue', ß: 'ss' };
    return (text || '')
      .toString()
      .replace(/[äÄöÖüÜß]/g, (m) => map[m] || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9\-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function slugifyLegacy(text) {
    return (text || '')
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9\-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function hideElement(el) {
    if (!el) return;
    el.classList.add('is-hidden');
    el.setAttribute('hidden', '');
  }
  function showElement(el) {
    if (!el) return;
    el.classList.remove('is-hidden');
    el.removeAttribute('hidden');
  }

  function fadeIn(el, duration = 180) {
    if (prefersReducedMotion()) {
      showElement(el);
      return;
    }
    const token = ++fadeToken;
    el.style.opacity = 0;
    showElement(el);
    let start;
    function step(ts) {
      if (token !== fadeToken) return;
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      el.style.opacity = p;
      if (p < 1) requestAnimationFrame(step);
      else el.style.opacity = '';
    }
    requestAnimationFrame(step);
  }

  function getTabFromUrl(key) {
    try {
      const url = new URL(window.location.href);
      return (
        url.searchParams.get(key ? `tab-${key}` : 'tab') ||
        (url.hash || '').replace(/^#tab-/, '') ||
        null
      );
    } catch {
      return null;
    }
  }

  /**
   * Aktualisiert die Fade-Gradienten für eine Tab-Komponente
   */
  function updateFadesForTabComponent(component) {
    const wrapper = component.querySelector('.qlf-tab-scroll-wrapper');
    const tabList = component.querySelector('.qlf-tab-list');
    if (!wrapper || !tabList) return;

    const maxScrollLeft = Math.max(tabList.scrollWidth - tabList.clientWidth, 0);
    const currentScrollLeft = Math.max(tabList.scrollLeft, 0);
    const atStart = currentScrollLeft <= SCROLL_EDGE_TOLERANCE;
    const atEnd = maxScrollLeft - currentScrollLeft <= SCROLL_EDGE_TOLERANCE;
    const noOverflow = maxScrollLeft <= SCROLL_EDGE_TOLERANCE;

    wrapper.classList.toggle('has-overflow', !noOverflow);

    if (noOverflow) {
      wrapper.classList.add('no-left-fade', 'no-right-fade', 'no-fade');
      return;
    }

    wrapper.classList.remove('no-fade');

    if (atStart) {
      wrapper.classList.add('no-left-fade');
    } else {
      wrapper.classList.remove('no-left-fade');
    }

    if (atEnd) {
      wrapper.classList.add('no-right-fade', 'no-fade');
    } else {
      wrapper.classList.remove('no-right-fade');
    }
  }

  function scheduleScrollFinalization(tabList, component, targetLeft) {
    const currentState = scrollFinalizeState.get(tabList);
    if (currentState?.timeoutId) window.clearTimeout(currentState.timeoutId);

    const token = (currentState?.token || 0) + 1;
    const finalize = () => {
      const latestState = scrollFinalizeState.get(tabList);
      if (!latestState || latestState.token !== token) return;
      tabList.scrollLeft = targetLeft;
      updateFadesForTabComponent(component);
    };

    const timeoutId = window.setTimeout(finalize, TAB_SCROLL_SETTLE_MS);
    scrollFinalizeState.set(tabList, { token, timeoutId });

    if ('onscrollend' in tabList) {
      tabList.addEventListener(
        'scrollend',
        () => {
          const latestState = scrollFinalizeState.get(tabList);
          if (!latestState || latestState.token !== token) return;
          window.clearTimeout(timeoutId);
          finalize();
        },
        { once: true }
      );
    }
  }

  /**
   * Scrollt zum aktiven Tab, sodass dieser sichtbar ist (linksbündig auf Mobile, zentriert auf Desktop)
   */
  function scrollToActiveTab(component, tabEl, forceImmediate = false) {
    const tabList = component.querySelector('.qlf-tab-list');
    if (!tabList || !tabEl) return;

    // Kein Scroll nötig wenn kein Overflow
    if (tabList.scrollWidth <= tabList.clientWidth) return;

    const isMobile = window.innerWidth < 768;
    let targetLeft;

    if (isMobile) {
      // Mobile: am tatsächlichen Listen-Inset ausrichten, damit der erste Tab nicht abgeschnitten wird
      const listStyles = window.getComputedStyle(tabList);
      const tabStyles = window.getComputedStyle(tabEl);
      const paddingStart =
        parseFloat(listStyles.getPropertyValue('padding-inline-start')) ||
        parseFloat(listStyles.paddingLeft) ||
        0;
      const marginStart =
        parseFloat(tabStyles.getPropertyValue('margin-inline-start')) ||
        parseFloat(tabStyles.marginLeft) ||
        0;
      targetLeft = Math.max(0, tabEl.offsetLeft - paddingStart - marginStart);
      if (!tabEl.previousElementSibling) targetLeft = 0;
    } else {
      // Desktop: Tab zentriert anzeigen
      const tabCenter = tabEl.offsetLeft + tabEl.offsetWidth / 2;
      targetLeft = tabCenter - tabList.clientWidth / 2;
    }

    // Begrenze auf gültigen Scroll-Bereich
    const maxScrollLeft = Math.max(tabList.scrollWidth - tabList.clientWidth, 0);
    targetLeft = Math.max(0, Math.min(targetLeft, maxScrollLeft));
    if (targetLeft <= SCROLL_EDGE_TOLERANCE) targetLeft = 0;
    if (maxScrollLeft - targetLeft <= SCROLL_EDGE_TOLERANCE) targetLeft = maxScrollLeft;

    // Skip wenn bereits an der richtigen Position
    if (Math.abs(tabList.scrollLeft - targetLeft) < 2) {
      updateFadesForTabComponent(component);
      return;
    }

    // Scroll (smooth oder instant)
    const behavior = forceImmediate || prefersReducedMotion() ? 'auto' : 'smooth';
    try {
      tabList.scrollTo({ left: targetLeft, behavior: behavior });
    } catch (e) {
      tabList.scrollLeft = targetLeft;
    }

    if (behavior === 'auto') {
      tabList.scrollLeft = targetLeft;
      updateFadesForTabComponent(component);
      requestAnimationFrame(() => updateFadesForTabComponent(component));
      return;
    }

    scheduleScrollFinalization(tabList, component, targetLeft);

    // Update Fades nach Scroll
    if (behavior === 'smooth') {
      setTimeout(() => updateFadesForTabComponent(component), 100);
      setTimeout(() => updateFadesForTabComponent(component), 250);
      setTimeout(() => updateFadesForTabComponent(component), 500);
    } else {
      setTimeout(() => updateFadesForTabComponent(component), 50);
    }
  }

  function initTabComponent(component) {
    const urlKey = component.getAttribute('data-url-key') || null;
    let activeTabPosition = 1;
    const tabMap = new Map();
    const panelMap = new Map();

    component.querySelectorAll('.tab-switcher').forEach((t) => {
      const pos = parseInt(t.dataset.tabPosition, 10);
      if (!Number.isNaN(pos)) tabMap.set(pos, t);
    });
    component.querySelectorAll('.tab-container').forEach((p) => {
      const pos = parseInt(p.dataset.tabPosition, 10);
      if (!Number.isNaN(pos)) panelMap.set(pos, p);
    });

    function updateAria(tabEl) {
      tabMap.forEach((t) => {
        const selected = t === tabEl;
        t.setAttribute('role', 'tab');
        t.setAttribute('aria-selected', selected ? 'true' : 'false');
        t.setAttribute('tabindex', selected ? '0' : '-1');
      });
      panelMap.forEach((p, pos) => {
        const match = pos === parseInt(tabEl.dataset.tabPosition, 10);
        p.setAttribute('role', 'tabpanel');
        if (match) {
          showElement(p);
        } else {
          hideElement(p);
        }
      });
    }

    function scrollTabIntoView(tabEl, force = false) {
      const isMobile = window.innerWidth < 768;
      if (!force && !isMobile && isFullyInViewport(tabEl)) return;
      scrollToActiveTab(component, tabEl);
    }

    function selectTabByPosition(
      pos,
      { withFade = false, withScroll = true, forceScroll = false, immediateScroll = false } = {}
    ) {
      if (!tabMap.has(pos) || !panelMap.has(pos)) return;
      const tabEl = tabMap.get(pos);
      tabMap.forEach((t) => t.classList.remove('active'));
      tabEl.classList.add('active');
      panelMap.forEach((panel, p) => {
        if (p === pos) {
          if (withFade) fadeIn(panel, 180);
          else showElement(panel);
        } else hideElement(panel);
      });
      activeTabPosition = pos;
      updateAria(tabEl);
      if (withScroll) {
        if (immediateScroll) {
          scrollToActiveTab(component, tabEl, true);
        } else {
          scrollTabIntoView(tabEl, forceScroll);
        }
      }
    }

    component.querySelectorAll('.tab-switcher').forEach((tabSwitcher) => {
      tabSwitcher.addEventListener('click', () => {
        const pos = parseInt(tabSwitcher.dataset.tabPosition, 10);
        if (!Number.isNaN(pos) && pos !== activeTabPosition) {
          selectTabByPosition(pos);
        }
      });
      tabSwitcher.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const pos = parseInt(tabSwitcher.dataset.tabPosition, 10);
          if (!Number.isNaN(pos) && pos !== activeTabPosition) {
            selectTabByPosition(pos);
          }
        }
      });
    });

    const tabList = component.querySelector('.qlf-tab-list');
    let pointerStartX = 0;
    let pointerStartScrollLeft = 0;
    const swipeThreshold = 50;
    tabList?.addEventListener(
      'pointerdown',
      (e) => {
        pointerStartX = e.clientX;
        pointerStartScrollLeft = tabList.scrollLeft;
      },
      { passive: true }
    );
    tabList?.addEventListener(
      'pointerup',
      (e) => {
        const scrollDelta = Math.abs(tabList.scrollLeft - pointerStartScrollLeft);
        if (e.pointerType !== 'touch' && e.pointerType !== 'pen') return;
        if (scrollDelta > POINTER_SCROLL_TOLERANCE) return;

        const dist = e.clientX - pointerStartX;
        if (Math.abs(dist) >= swipeThreshold) {
          const positions = Array.from(tabMap.keys()).sort((a, b) => a - b);
          const currentIndex = positions.indexOf(activeTabPosition);
          let newIndex = currentIndex;
          if (dist > 0) newIndex = clamp(currentIndex - 1, 0, positions.length - 1);
          if (dist < 0) newIndex = clamp(currentIndex + 1, 0, positions.length - 1);
          if (newIndex !== currentIndex) selectTabByPosition(positions[newIndex]);
        }
      },
      { passive: true }
    );

    // Fade-Handling initialisieren
    const initFadeHandling = () => {
      if (!tabList) return;

      // Initial-Update
      updateFadesForTabComponent(component);

      // Scroll-Event
      tabList.addEventListener('scroll', () => updateFadesForTabComponent(component), {
        passive: true
      });

      // Resize-Events
      const rerun = () => updateFadesForTabComponent(component);
      window.addEventListener('resize', rerun);
      window.addEventListener('load', rerun);
    };

    initFadeHandling();

    (function initFromUrl() {
      const val = getTabFromUrl(urlKey);
      if (val) {
        const pos = parseInt(val, 10);
        if (!Number.isNaN(pos) && tabMap.has(pos)) {
          selectTabByPosition(pos, { withScroll: true, immediateScroll: true });
          scrollComponentIntoViewTop(component);
          return;
        }
        const targetSlug = slugify(val);
        const targetLegacy = slugifyLegacy(val);
        let foundPos = null;
        tabMap.forEach((t, pos) => {
          const label = (t.textContent || '').trim();
          const s1 = slugify(label),
            s2 = slugifyLegacy(label);
          if (
            !foundPos &&
            (s1 === targetSlug || s1 === targetLegacy || s2 === targetSlug || s2 === targetLegacy)
          ) {
            foundPos = pos;
          }
        });
        if (foundPos) {
          selectTabByPosition(foundPos, { withScroll: true, immediateScroll: true });
          scrollComponentIntoViewTop(component);
          return;
        }
      }
      const first = Math.min(...tabMap.keys());
      if (!Number.isNaN(first)) {
        selectTabByPosition(first, { withScroll: true, immediateScroll: true });

        // Zusätzlich: Sofort zum ersten Tab scrollen (wichtig für Mobile)
        requestAnimationFrame(() => {
          const firstTab = tabMap.get(first);
          if (firstTab) {
            scrollToActiveTab(component, firstTab, true);
          }
        });

        // Nochmal nach dem Laden sicherstellen
        window.addEventListener(
          'load',
          () => {
            const firstTab = tabMap.get(first);
            if (firstTab) {
              scrollToActiveTab(component, firstTab, true);
            }
          },
          { once: true }
        );
      }
    })();

    if (!navListenersBound) {
      navListenersBound = true;
      const syncAllFromUrl = () => {
        document.querySelectorAll('.qlf-tab-component').forEach((c) => {
          const key = c.getAttribute('data-url-key') || null;
          const val = getTabFromUrl(key);
          if (!val) return;
          const pos = parseInt(val, 10);
          const map = new Map();
          c.querySelectorAll('.tab-switcher').forEach((t) => {
            const p = parseInt(t.dataset.tabPosition, 10);
            if (!Number.isNaN(p)) map.set(p, t);
          });
          if (!Number.isNaN(pos) && map.has(pos)) {
            const evt = new Event('click');
            map.get(pos).dispatchEvent(evt);
          } else if (val) {
            const targetSlug = slugify(val);
            const targetLegacy = slugifyLegacy(val);
            let target;
            c.querySelectorAll('.tab-switcher').forEach((t) => {
              if (target) return;
              const label = (t.textContent || '').trim();
              const s1 = slugify(label),
                s2 = slugifyLegacy(label);
              if (
                s1 === targetSlug ||
                s1 === targetLegacy ||
                s2 === targetSlug ||
                s2 === targetLegacy
              )
                target = t;
            });
            if (target) target.dispatchEvent(new Event('click'));
          }
        });
      };
      window.addEventListener('popstate', syncAllFromUrl);
      window.addEventListener('hashchange', syncAllFromUrl);
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.qlf-tab-component').forEach(initTabComponent);
  });
})();
